import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import axios from 'axios';

import { ItemCategorizationModal } from '@admin/components/items/item_categorization_modal';
import { ItemGroupableGrid } from '@admin/components/items/item_groupable_grid';
import { ItemsTable } from '@admin/components/items/items_table';
import { ItemsCount } from '@admin/components/items_count';
import { Spinner } from '@admin/components/spinner';
import { useTitle } from '@admin/hooks/use_title';
import type { IItem } from '@admin/types/item';

interface AccountData {
  id: number;
  number: string;
  customer: { name: string };
}

interface OrderGroup {
  order: {
    id: number;
    account_id: number;
    number: string;
    scheduled: string;
    type: string;
    subtype: string;
    maintenance?: boolean;
  };
  items: IItem[];
}

export const BulkCategorize: React.FC = () => {
  useTitle('Update Items');
  const { accountID } = useParams<{ accountID: string }>();
  const [account, setAccount] = useState<AccountData | null>(null);
  const [groupedItems, setGroupedItems] = useState<OrderGroup[] | null>(null);
  const [selectedItems, setSelectedItems] = useState<IItem[]>([]);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!accountID) {
      setAccount(null);
      setGroupedItems(null);
      setSelectedItems([]);
      setShowModal(false);
      return;
    }

    setAccount(null);
    setGroupedItems(null);
    setSelectedItems([]);
    setShowModal(false);

    let cancelled = false;
    axios.get<AccountData>(`/accounts/${accountID}.json`).then((res) => {
      if (!cancelled) setAccount(res.data);
    });
    axios.get<OrderGroup[]>(`/accounts/${accountID}/items/groupables.json`).then((res) => {
      if (!cancelled) setGroupedItems(res.data);
    });
    return () => {
      cancelled = true;
    };
  }, [accountID]);

  const handleConfirm = useCallback(() => {
    setShowModal(true);
  }, []);

  const handleModalSave = useCallback(() => {
    setShowModal(false);
    window.location.href = `/accounts/${accountID}/inventory`;
  }, [accountID]);

  const handleModalClose = useCallback(() => {
    setShowModal(false);
  }, []);

  if (!accountID) return null;

  return (
    <>
      <h1 className="page-header text-overflow">
        <ol className="breadcrumb">
          <li>
            <NavLink to="/accounts">Accounts</NavLink>
          </li>
          {account && (
            <li>
              <NavLink to={`/accounts/${account.id}`}>
                #{account.number} ({account.customer.name})
              </NavLink>
            </li>
          )}
          {account && (
            <li>
              <NavLink to={`/accounts/${account.id}/inventory`}>Inventory</NavLink>
            </li>
          )}
          <li className="active">Bulk Categorize</li>
        </ol>
      </h1>

      <Spinner visible={!groupedItems} />

      {groupedItems && (
        <ItemGroupableGrid
          groupedItems={groupedItems}
          selections={selectedItems}
          onSelectionsChange={setSelectedItems}
        />
      )}

      {groupedItems && (
        <div className="panel">
          <div className="panel-heading">
            <div className="panel-title">
              <ItemsCount items={selectedItems} />
            </div>
          </div>
          <ItemsTable items={selectedItems} />
          <div className="panel-footer">
            <div className="clearfix">
              <div className="pull-right">
                <a href={`/accounts/${accountID}/inventory`} className="btn btn-default">
                  Cancel
                </a>
                <button className="btn btn-primary" disabled={selectedItems.length === 0} onClick={handleConfirm}>
                  Select Category
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <ItemCategorizationModal
          items={selectedItems}
          accountID={accountID}
          mode="categorize"
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}
    </>
  );
};
